/**
 * Empty-state illustration for the Story Manager details panel: a tilted
 * document under a magnifying glass, with a few playful accents (squiggle,
 * sparkles, dots). Styled entirely with brand tokens (`--primary` green,
 * `--accent` gold) + neutral surfaces via inline `style` (presentation
 * attributes don't resolve `var()`), so it follows light/dark theming.
 */
const EmptyDetailsIllustration = () => (
  <svg
    viewBox="0 0 200 160"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ width: '100%', height: 'auto', display: 'block' }}
    aria-hidden="true"
  >
    {/* Decorative rounded square, tilted, peeking from behind the document. */}
    <rect
      x="118"
      y="82"
      width="44"
      height="44"
      rx="13"
      transform="rotate(10 140 104)"
      style={{ fill: 'color-mix(in srgb, var(--accent) 15%, transparent)' }}
    />

    {/* Hand-drawn squiggle flourish, lower-left. */}
    <path
      d="M34 120 Q43 108 52 120 T70 120"
      fill="none"
      style={{
        stroke: 'color-mix(in srgb, var(--text-muted) 60%, transparent)',
      }}
      strokeWidth="3"
      strokeLinecap="round"
    />

    {/* Document with a folded top-right corner — tilted a touch for energy. */}
    <g transform="rotate(-6 112 78)">
      <path
        d="M84 30 H126 L148 52 V120 Q148 128 140 128 H84 Q76 128 76 120 V38 Q76 30 84 30 Z"
        style={{ fill: 'var(--surface)', stroke: 'var(--border)' }}
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M126 30 L148 52 H126 Z"
        style={{
          fill: 'color-mix(in srgb, var(--text-muted) 16%, var(--surface))',
          stroke: 'var(--border)',
        }}
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <g
        style={{
          stroke: 'color-mix(in srgb, var(--text-muted) 50%, transparent)',
        }}
        strokeWidth="3"
        strokeLinecap="round"
      >
        <line x1="92" y1="50" x2="138" y2="50" />
        <line x1="92" y1="64" x2="138" y2="64" />
        <line x1="92" y1="78" x2="122" y2="78" />
      </g>
    </g>

    {/* Brand-green accent star, top-left. */}
    <path
      d="M52 31 Q52 40 61 40 Q52 40 52 49 Q52 40 43 40 Q52 40 52 31 Z"
      style={{ fill: 'var(--primary)' }}
    />

    {/* Sparkles for a bit of fun — gold top-right, green bottom-right. */}
    <path
      d="M162 38 Q162 46 170 46 Q162 46 162 54 Q162 46 154 46 Q162 46 162 38 Z"
      style={{ fill: 'var(--accent)' }}
    />
    <path
      d="M150 133 Q150 138 155 138 Q150 138 150 143 Q150 138 145 138 Q150 138 150 133 Z"
      style={{ fill: 'var(--primary)' }}
    />

    {/* Magnifying glass — lens over the document, gold ring + handle. */}
    <circle cx="84" cy="96" r="30" style={{ fill: 'var(--background)' }} />
    <circle
      cx="84"
      cy="96"
      r="30"
      fill="none"
      style={{ stroke: 'var(--accent)' }}
      strokeWidth="5"
    />
    <line
      x1="105"
      y1="117"
      x2="128"
      y2="140"
      style={{ stroke: 'var(--accent)' }}
      strokeWidth="5"
      strokeLinecap="round"
    />
  </svg>
);

export default EmptyDetailsIllustration;
